import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'antd';
import { CalendarOutlined, GoogleOutlined, AppleOutlined, WindowsOutlined, YahooOutlined } from '@ant-design/icons';
import tzlookup from 'tz-lookup';
const AddToCalendar = ({
    title,
    description,
    dates,
    dateIndex,
    start_time,
    end_time,
    location,
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [timezone, setTimezone] = useState('UTC');

    useEffect(() => {
        const detectTimezone = async () => {
            try {
                // Use OpenStreetMap's Nominatim API
                const encodedAddress = encodeURIComponent(location);
                const response = await fetch(
                    `https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}`,
                    {
                        headers: {
                            'Accept': 'application/json',
                            'User-Agent': 'EventPlatform' // Required by Nominatim's terms of use
                        }
                    }
                );

                const data = await response.json();

                if (data && data[0]) {
                    const { lat, lon } = data[0];
                    // Get timezone from coordinates
                    const detectedTimezone = tzlookup(parseFloat(lat), parseFloat(lon));
                    setTimezone(detectedTimezone);
                } else {
                    // Fallback to browser's timezone
                    setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
                }
            } catch (error) {
                console.error('Error detecting timezone:', error);
                // Fallback to browser's timezone
                setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
            }
        };

        if (location) {
            detectTimezone();
        }
    }, [location]);

    // Helper to combine date and time strings into a Date object
    const createDateTime = (dateStr, timeStr) => {
        const [hours, minutes, seconds] = timeStr.split(':');
        const date = new Date(dateStr);
        date.setHours(parseInt(hours, 10), parseInt(minutes, 10), parseInt(seconds, 10));
        return date;
    };

    // Format date for iCal
    const formatICalDate = (date) => {
        return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const eventDate = dates[dateIndex];
    const startDateTime = createDateTime(eventDate, start_time);
    const endDateTime = createDateTime(eventDate, end_time);

    const getGoogleUrl = () => {
        const params = new URLSearchParams({
            action: 'TEMPLATE',
            text: title,
            details: description,
            location,
            dates: `${formatICalDate(startDateTime)}/${formatICalDate(endDateTime)}`,
            ctz: timezone
        });
        return `https://calendar.google.com/calendar/render?${params.toString()}`;
    };

    const getOutlookUrl = () => {
        const params = new URLSearchParams({
            path: '/calendar/action/compose',
            rru: 'addevent',
            subject: title,
            body: description,
            location,
            startdt: startDateTime.toISOString(),
            enddt: endDateTime.toISOString()
        });
        return `https://outlook.live.com/calendar/0/${params.toString()}`;
    };

    const getYahooUrl = () => {
        const params = new URLSearchParams({
            v: 60,
            title,
            desc: description,
            in_loc: location,
            st: formatICalDate(startDateTime),
            et: formatICalDate(endDateTime)
        });
        return `https://calendar.yahoo.com/?${params.toString()}`;
    };

    const getICalContent = () => {
        const event = [
            'BEGIN:VCALENDAR',
            'VERSION:2.0',
            'BEGIN:VEVENT',
            `DTSTART:${formatICalDate(startDateTime)}`,
            `DTEND:${formatICalDate(endDateTime)}`,
            `SUMMARY:${title}`,
            `DESCRIPTION:${description}`,
            `LOCATION:${location}`,
            'END:VEVENT',
            'END:VCALENDAR'
        ].join('\n');

        return `data:text/calendar;charset=utf8,${encodeURIComponent(event)}`;
    };

    const calendars = [
        {
            name: 'Google Calendar',
            icon: <GoogleOutlined />,
            url: getGoogleUrl(),
            description: 'Add to your Google Calendar'
        },
        {
            name: 'Apple Calendar',
            icon: <AppleOutlined />,
            url: getICalContent(),
            description: 'Download .ics file for Apple Calendar'
        },
        {
            name: 'Outlook',
            icon: <WindowsOutlined />,
            url: getOutlookUrl(),
            description: 'Add to your Outlook Calendar'
        },
        {
            name: 'Yahoo Calendar',
            icon: <YahooOutlined />,
            url: getYahooUrl(),
            description: 'Add to your Yahoo Calendar'
        }
    ];

    return (
        <>
            <Button
          
                icon={<CalendarOutlined />}
                onClick={() => setIsModalOpen(true)}
                className="ticket-btn"
            />

            <Modal
                title="Add to Calendar"
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={null}
                width={400}
                // className="calendar-modal glassmorphic-modal"
                // styles={{
                //     content: {
                //         backgroundColor: 'rgba(50, 50, 50, 0.95)',
                //         backdropFilter: 'blur(10px)',
                //         border: '1px solid rgba(255, 255, 255, 0.2)',
                //         borderRadius: '8px',
                //     },
                //     header: {
                //         backgroundColor: 'transparent',
                //         borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                //     },
                //     body: {
                //         backgroundColor: 'transparent',
                //     }
                // }}
            >
                <div className="calendar-options-container">
                    {calendars.map((calendar) => (
                        <a
                            key={calendar.name}
                            href={calendar.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setIsModalOpen(false)}
                            className="calendar-option"
                        >
                            <span className="calendar-icon-wrapper">{calendar.icon}</span>
                            <div>
                                <div className="calendar-name">{calendar.name}</div>
                                <div className="calendar-description">{calendar.description}</div>
                            </div>
                        </a>
                    ))}
                </div>
            </Modal>
        </>
    );
};

export default AddToCalendar;